import { runInContextTransaction } from '../graphics/canvasUtils';
import { IRenderContext } from '../graphics/renderContext';
import { convertUnits, PrintUnits } from './print';
import { ImageLayer, VideoLayer, MapLayer, LayerType } from './layers';

export interface PlaceholderOverlayRendererText {
    imageLayer: string;
    mapLayer: string;
    videoLayer: string;
}

export type OverlaySupportedLayer = ImageLayer | MapLayer | VideoLayer;

export class PlaceholderOverlayRenderer {
    protected readonly textFontFamily = 'Arimo, Arial, sans-serif';

    protected readonly iconFontFamily = 'FontAwesome';

    public constructor(protected readonly text: PlaceholderOverlayRendererText) {}

    // eslint-disable-next-line max-statements
    public render(ctx: IRenderContext, layer: OverlaySupportedLayer): void {
        if (!layer.width || !layer.height) {
            return;
        }

        let lineWidth = 8;
        let centerSpacing = 60;
        let fontSizePixels = convertUnits(26, PrintUnits.points, PrintUnits.pixels);
        const icon = String.fromCharCode(parseInt(layer.type === LayerType.map ? 'F278' : 'F0EE', 16));
        const text = this.getText(layer);

        ctx.fontFamily(this.textFontFamily).fontSize(fontSizePixels, 'px');
        const textWidth = ctx.textWidth(text);

        let diff = null;

        if (layer.width <= textWidth) {
            diff = layer.width / textWidth;
        } else if (layer.height <= fontSizePixels) {
            diff = layer.height / fontSizePixels;
        }

        if (diff) {
            lineWidth *= diff;
            centerSpacing *= diff;
            fontSizePixels *= diff;
            ctx.fontFamily(this.textFontFamily).fontSize(fontSizePixels, 'px');
        }

        runInContextTransaction(ctx, () => this.darkenLayer(ctx, layer));

        ctx.strokeColor('#fff').lineWidth(lineWidth).lineCap('round').fillColor('#fff');

        this.renderFull(ctx, layer, fontSizePixels, icon, lineWidth, centerSpacing, text);
    }

    protected renderLines(
        ctx: IRenderContext,
        layer: OverlaySupportedLayer,
        lineWidth: number,
        centerSpacing: number,
    ): void {
        const lines = [
            [
                { x: lineWidth, y: lineWidth },
                { x: layer.width / 2 - centerSpacing, y: layer.height / 2 - centerSpacing },
            ],
            [
                { x: layer.width - lineWidth, y: lineWidth },
                { x: layer.width / 2 + centerSpacing, y: layer.height / 2 - centerSpacing },
            ],
            [
                { x: layer.width - lineWidth, y: layer.height - lineWidth },
                { x: layer.width / 2 + centerSpacing, y: layer.height / 2 + centerSpacing },
            ],
            [
                { x: lineWidth, y: layer.height - lineWidth },
                { x: layer.width / 2 - centerSpacing, y: layer.height / 2 + centerSpacing },
            ],
        ];

        for (const line of lines) {
            ctx.drawLine(...line);
        }
    }

    protected darkenLayer(ctx: IRenderContext, layer: OverlaySupportedLayer): void {
        ctx.opacity(0.25).beginPath().rect({ x: 0, y: 0, width: layer.width, height: layer.height }).fill('#000');
    }

    protected getText(layer: OverlaySupportedLayer): string {
        switch (layer.type) {
            case LayerType.image:
                return this.text.imageLayer.toLocaleUpperCase();
            case LayerType.map:
                return this.text.mapLayer.toLocaleUpperCase();
            case LayerType.video:
                return this.text.videoLayer.toLocaleUpperCase();
            default:
                return '';
        }
    }

    // eslint-disable-next-line max-params
    protected renderFull(
        ctx: IRenderContext,
        layer: OverlaySupportedLayer,
        fontSizePixels: number,
        icon: string,
        lineWidth: number,
        centerSpacing: number,
        text: string,
    ): void {
        this.renderLines(ctx, layer, lineWidth, centerSpacing);

        const iconFontSizePixels = fontSizePixels * 2;

        ctx.fontFamily(this.iconFontFamily).fontSize(iconFontSizePixels, 'px');
        const iconWidth = ctx.textWidth(icon);

        const lineHeightMultiplier = 1;

        const totalCenterHeight = iconFontSizePixels + fontSizePixels * lineHeightMultiplier;

        ctx.text({
            fill: true,
            position: {
                x: layer.width / 2 - iconWidth / 2,
                y: layer.height / 2 - totalCenterHeight / 2,
            },
            stroke: false,
            text: icon,
        });

        ctx.fontFamily(this.textFontFamily).fontSize(fontSizePixels, 'px');

        const textWidth = ctx.textWidth(text);

        ctx.text({
            fill: true,
            position: {
                x: layer.width / 2 - textWidth / 2,
                y:
                    layer.height / 2 -
                    totalCenterHeight / 2 +
                    iconFontSizePixels +
                    fontSizePixels * (lineHeightMultiplier - 1),
            },
            stroke: false,
            text,
        });
    }
}
